import { MediaObjectUrl } from '../media/types'
import { BatchSearch } from '../shared/batch.types'
import { DateTimeString, PaginatedSearch, WithPermissions } from '../shared/types'
import { AnyRuleSet, PublicStaffProfile } from './staff'

export type Token = {
  session: string
}

export enum BuiltInLicenseTypeEnum {
  TIER_ZERO = 'tierZero',
  TIER_ONE = 'tierOne',
  TIER_TWO = 'tierTwo',
  UNRESTRICTED = 'unrestricted',
}

/**
 * API Auth types
 */
export type Tenant = WithPermissions<{
  id: string
  name: string
  accountNumber: number
  logoUrls: MediaObjectUrl
  licenseType: BuiltInLicenseTypeEnum
}>

export type Credentials = {
  email?: string
  employeeId?: string
  password: string
  domain: string
}

export type LoginResponse = {
  data: Token & MFAResponse
}

export type TenantLoginRedirect = {
  state: string
  session: string
  domain: string
}

export enum LoginStrategyStatusEnum {
  EXPIRED = 'expired',
  NEW = 'new',
}

export type LoginStrategyPassword = {
  strategy: 'password'
  employeeId: boolean
  status?: LoginStrategyStatusEnum
}
export type LoginStrategySaml = { strategy: 'saml'; redirectUrl: string; displayName: string }

export type LoginStrategy = LoginStrategyPassword | LoginStrategySaml

export type AuthFactorType = 'sms' | 'email' | 'totp'

export type AuthFactor = {
  id: string
  authFactorType: AuthFactorType
  userId: string
  secrets: string
  activatedAt: string | null
  createdAt: string
  deletedAt: string | null
  displayName: string
  __entity_type__: 'authFactor'
  __permissions__: string[]
}

export type User = WithPermissions<{
  id: string
  email: string
  createdAt: string
  updatedAt: string
}>

export type MFAResponse = {
  authFactors: AuthFactor[]
  session: string
  sessionId: string
}

export type InitiateChallenge = {
  challengeId: string
}

export type SamlProvider = WithPermissions<{
  id: string
  activatedAt: string | null
  createdAt: string
  metadata: {
    displayName: string
  }
  tenantId: string
  serviceProviderInfo?: {
    acsUrl: string
    entityId: string
    nameIdFormat: string
  }
}>

export enum BindingMethodEnum {
  HTTP_POST = 'HTTP-POST',
  HTTP_REDIRECT = 'HTTP-Redirect',
}

// Supports creating a SAML provider with either an uploaded metadata file OR explicit metadata fields
export type CreateSamlProviderParams =
  | {
      samlProviderId: string
      displayName: string
      active: boolean
      file: File
    }
  | {
      samlProviderId: string
      displayName: string
      active: boolean
      entityId: string
      x509Certificate: string
      bindingUrl: string
      bindingMethod: BindingMethodEnum
    }

export enum BuiltinRolesEnum {
  ADMIN = 'admin',
  EMPLOYEE = 'employee',
}

export enum FeatureRolesEnum {
  USER_MANAGEMENT = 'f:usrMgmt',
  BUDGET = 'f:bdgt',
  INTEGRATION = 'f:intg',
}

export enum TenantStatusEnum {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum StaffProvisionedEnum {
  TRUE = 'true',
  FALSE = 'false',
}

export type TenantMember = WithPermissions<{
  id: string
  firstName: string
  lastName: string
  tenantId: string
  userId: string
  role: string
  suspendedAt: string | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
  lastLoginAt: string | null
  lastInvitedAt: string | null
  harmonizedUpdatedAt: string
  __entity_type__: 'tenantMember'
  // Embeds
  email?: string | null
  completedMfaSetup?: boolean | null
  avatarUrls?: MediaObjectUrl | null
  publicStaffProfile?: PublicStaffProfile | null
  hasReports?: boolean | null
  teamCount?: number
  canReceivePoints?: boolean | null
  directReportCount?: number
  // 2-character ISO country code from the staff record
  country?: string | null
}>

export type Locale = {
  language: string | null
  timezone: string | null
}

export type EditLocaleParams = {
  language: string | null
  timezone: string | null
}

export type TenantAuthSetting = {
  tenantId: string
  mfaRequired: boolean
  ssoRequiredIfEnabled: boolean
  justInTimeProvisioning: boolean
  maxPasswordAgeMonths: number | null
  sessionDurationDays: number
  autoInviteStaff?: boolean
  autoInviteRuleSet?: AnyRuleSet | null
}

export type TenantMemberEmbeds =
  | 'email'
  | 'completedMfaSetup'
  | 'avatar'
  | 'publicStaffProfile'
  | 'hasReports'
  | 'teamCount'
  | 'canReceivePoints'
  | 'directReportCount'
  | 'country'

export type GetTenantMemberParams = {
  embeds: TenantMemberEmbeds[]
}

export type CreateTenantMemberParams = {
  // Required
  role: string
  email: string | null
  firstName: string
  lastName: string
  sendWelcome: boolean
  // Optional
  employeeId?: string
  managerRef?: string // leave blank for no manager
  jobTitle?: string
  startDate?: string
  birthDate?: string
  terminationDate?: string
  phoneNumber?: string
  country?: string | null // valid 2-digit country code
  location?: string
  state?: string
  businessUnit?: string
  group?: string
  department?: string
  costCenter?: string
}

export type UpdateTenantMemberParams = Partial<CreateTenantMemberParams>

export type UpdateTenantMemberRoleParams = {
  role: string
}

export enum TeamSegmentTypeEnum {
  COUNTRY = 'country',
  REGION = 'region',
  LOCATION = 'location',
  DEPARTMENT = 'department',
  COST_CENTER = 'costCenter',
  BUSINESS_UNIT = 'businessUnit',
  GROUP = 'group',
}

/** Values from `GET /identity/teams/types` and `listTeams({ segmentTypeIn })`
 *  @example const teamTypes = [
 'noSegment', // automated OR manual teams
 'country',
 'region',
 'location',
 'department',
 'costCenter',
 'businessUnit',
 'group',
]
 */
export type TeamListSegmentType = 'noSegment' | TeamSegmentTypeEnum

export enum TeamTypeEnum {
  MANUAL = 'manual',
  AUTOMATED = 'automated',
  QUICK = 'quick',
}

export enum TenantMemberStatusEnum {
  INVITED = 'invited',
  JOINED = 'joined',
  NOT_INVITED = 'notInvited',
  ARCHIVED = 'archived',
}

export type Team = WithPermissions<{
  id: string
  name: string
  description: string
  tenantId: string
  createdAt: string
  updatedAt: string
  hidden: boolean
  // embeds
  memberCount: number
  teamType: TeamTypeEnum
  segmentType: TeamSegmentTypeEnum
  teamAutomation: TeamAutomation | null
  activeMemberCount: number
  __entity_type__: 'team'
}>

export type TeamAutomation = WithPermissions<{
  id: string
  teamId: string
  tenantId: string
  notes: string
  ruleSet: AnyRuleSet
  info: string
  segmentType: string | null
  segmentRef: string | null
  activatedAt: string
  createdAt: string
  lastRunAt: string
  matched: number
  __entity_type__: 'teamAutomation'
}>

export type ListTeamsEmbeds = 'types' | 'memberCount' | 'teamAutomation' | 'activeMemberCount'
export type TeamTypes = 'manual' | 'automated' | 'quick'
export type ListTeamsParams = PaginatedSearch<
  {
    idIn?: string[]
    automated?: boolean
    search?: string
    teamTypeIn?: TeamTypes[]
    memberIdIn?: string[]
    hidden?: boolean
    segmentTypeIn?: TeamListSegmentType[]
  },
  ListTeamsEmbeds
>

export type CreateTeamParams = {
  name: string
  description?: string
  teamType: 'manual' | 'automated'
  hidden: boolean
}

export type UpdateTeamParams = Omit<CreateTeamParams, 'teamType'>

export type ListTenantMembersParams = PaginatedSearch<
  {
    suspended?: boolean
    roleIn?: string[]
    statusIn?: TenantMemberStatusEnum[]
    search?: string
    idIn?: string[]
    team?: { teamIdIn: string[] }
    teamIdIn?: string[]
    notIdIn?: string[]
    tenantIdIn?: string[]
    userIdIn?: string[]
    emailIn?: string[]
    tenantStatusIn?: TenantStatusEnum[]
    hasReports?: boolean
    directManagerIdIn?: string[]
    indirectManagerIdIn?: string[]
    staffRecord?: {
      groupIdIn?: string[]
    }
    jobTitleIn?: string[]
    directReportsOnly?: boolean
    departmentIdIn?: string[]
    hasBirthDate?: true
    hasAnniversary?: true
    hasEmail?: boolean
  },
  TenantMemberEmbeds
>

export type BatchAddToTeamsSearchParams = BatchSearch<ListTenantMembersParams>

export type AddTenantMembersToTeamsParams = Omit<BatchAddToTeamsSearchParams, 'team'> & {
  teamIds?: string[]
  team?: {
    name: string
    description: string
    hidden?: boolean
  }
}

export type BatchSuspendTenantMembersParams = BatchSearch<ListTenantMembersParams>

export type BatchResendWelcomeEmailParams = BatchSearch<ListTenantMembersParams>

export type BatchUnsuspendTenantMembersParams = BatchSearch<ListTenantMembersParams>

export type DeleteTenantMembersResult = { success: number; failure: number }

export type TenantMembersBulkFilterParams = {
  idIn?: string[]
  search?: string
  roleIn?: string[]
  statusIn?: TenantMemberStatusEnum[]
  suspended?: boolean
  departmentIdIn?: string[]
  hasReports?: boolean
  jobTitleIn?: string[]
  teamIdIn?: string[]
  notIdIn?: string[]
  emailIn?: string[]
  directReportsOnly?: true
  directManagerIdIn?: string[]
  hasBirthDate?: true
  hasAnniversary?: true
}

export type DeleteTenantMembersParams = TenantMembersBulkFilterParams & {
  deleteCount: number
}

export type ExportOfflineCodesParams = TenantMembersBulkFilterParams & {
  durationDays: number
}

export type TeamMember = {
  id: string
  tenantMemberId: string
  teamId: string
  createdAt: Date
  updatedAt: Date
  roles: string[]
  memberRole: BuiltinRolesEnum
  //embeds
  memberDisplayData?: TenantMemberDisplayData
  __entity_type__: 'teamMember'
}
export type ListTeamMembersEmbeds = 'members'

export type TeamMembersParams = {
  idIn?: string[]
  teamIdIn?: string[]
  search?: string
  suspended?: boolean
}

export type ListTeamMembersParams = PaginatedSearch<TeamMembersParams, ListTeamMembersEmbeds>

export type BatchRemoveMembersSearchParams = BatchSearch<ListTeamMembersParams>

export type TenantMemberAvatar = {
  id: string
  tenantMemberId: string
  mediaObjectId: string
  createdAt: string
  updatedAt: string
  __entity_type__: 'tenantMemberAvatar'
}

// Types for the federated search
export type TenantMemberDisplayData = {
  id: string
  email: string | null
  firstName: string
  lastName: string
  active: boolean
  avatarUrls: null | MediaObjectUrl
  jobTitle: string
}

export type TimelineTenantMemberDisplayData = {
  id: string
  email: string | null
  firstName: string
  lastName: string
  active: boolean
  avatarUrls: null | MediaObjectUrl
}

export type TeamDisplayData = {
  id: string
  name: string
  segmentType: TeamSegmentTypeEnum | null
  teamType: TeamTypeEnum
  activeMemberCount: number
}

export type TimelineTeamDisplayData = {
  id: string
  name: string
}

export type TenantMemberOrTeamDisplayData =
  | TenantMemberDisplayData
  | TeamDisplayData
  | TimelineTeamDisplayData
  | TimelineTenantMemberDisplayData

export type DepartmentDisplayData = {
  id: string
  name: string
}

export type TenantMembersCountParams = BatchAddToTeamsSearchParams

export type TenantMembersCount = {
  total: number
}

export type TenantLogo = {
  id: string
  tenantId: string
  mediaObjectId: string | null
  createdAt: string
  updatedAt: string
  __entity_type__: 'tenantLogo'
}

export type TotalPasswordsExpiredCount = {
  total: number
}

export interface TeamSegment {
  tenantId: string
  type: TeamSegmentTypeEnum
  hidden: boolean
  lastExecutedAt: Date | null
  createdAt: Date
  __entity_type__: 'teamSegment'
}

export type TeamSegmentsPreview = {
  type: TeamSegmentTypeEnum
  total: number
}

export type TeamSegmentTypePreviewParams = PaginatedSearch<{ teamSegmentType: TeamSegmentTypeEnum }>

export type TeamSegmentTypePreview = {
  name: string
  total: number
}

export type TeamSegmentParam = Pick<TeamSegment, 'type' | 'hidden'>

export type TeamsSegmentsSyncStatus = {
  lastSyncedAt: Date
}

export type TenantBanner = {
  id: string
  tenantId: string
  mediaObjectId: string | null
  createdAt: Date
  updatedAt: Date
  __entity_type__: 'tenantBanner'
}

export enum MergeDevFieldImpactEnum {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type MergeDevFieldStatus = {
  field: string
  name: string
  total: number
  synced: number
  impact: MergeDevFieldImpactEnum
  description: string
}

export type MergeDevFieldsData = {
  staffGroup: MergeDevFieldStatus[]
  staffLocation: MergeDevFieldStatus[]
  staffRecord: MergeDevFieldStatus[]
}

export type MicrosoftTeamsApiDetail = {
  apiUrl: string
}

export type MicrosoftTeamsIdentity = {
  createdAt: Date
  updatedAt: Date
  memberId: string
  oid: string
  tid: string
}

export type TenantMembersActivationStats = {
  activated: number
  invited: number
  total: number
}

export type SlackIdentity = {
  slackUrl: string
}

export type SetSecondaryEmailParams = {
  email: string
}

/**
 * A member's secondary ("rewards") email — used when their staff/member record
 * has no email, so deskless users can still receive redemption communications.
 *
 * Mirrors the `SecondaryEmail` serializer/model on `syndicate`
 * (`PUT /identity/secondaryEmails`, SYN-7292/SYN-7280).
 */
export type SecondaryEmail = {
  tenantMemberId: string
  email: string
  createdAt: DateTimeString
  updatedAt: DateTimeString
  __entity_type__: 'secondaryEmail'
}
